export default function BoyntonBeachDestinationsSection() {
  const stats = [
    {
      value: "55%",
      label: "of Boynton Beach requests are Caribbean cruise or Bahamas cruise departures with under 3 weeks notice",
    },
    {
      value: "2–4 weeks",
      label: "average notice for business trips to Latin America from Boynton Beach professionals",
    },
    {
      value: "Nov–Apr",
      label: "peak cruise and snowbird season — passport renewals spike before holiday and spring break sailings",
    },
    {
      value: "36-hour",
      label: "shortest cruise departure window we've accommodated for a Boynton Beach family",
    },
  ];

  const destinations = [
    {
      icon: "ri-map-pin-2-line",
      name: "The Bahamas & Caribbean",
      context: "The most common destination for Boynton Beach travelers — whether by short Bahamas cruise from the Port of Palm Beach (Margaritaville at Sea), or by major Caribbean cruise from Port Everglades in Fort Lauderdale. Families, retirees, and snowbirds from Boynton Beach regularly sail to the Bahamas, Jamaica, the Cayman Islands, and the Eastern Caribbean. All cruise passengers and most air travelers need valid passports.",
      urgency: "Cruise embarkation deadlines are immovable — passport issues discovered days before sailing require emergency processing",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "Mexico",
      context: "Cancun, Riviera Maya, and Los Cabos are staple destinations for Boynton Beach families seeking beach vacations within a short flight. PBI offers connecting service through Atlanta, Charlotte, and Houston. Business travelers also fly to Mexico City and Guadalajara for corporate meetings. Mexico requires six months of passport validity for entry.",
      urgency: "Spring break bookings surge in January — passport discoveries spike in February and March",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "Canada",
      context: "Toronto and Montreal draw Boynton Beach snowbirds and business travelers. Many retirees have family in Ontario and Quebec and travel north during summer months. Canadian ski destinations like Whistler and Banff attract winter sports enthusiasts. Canada requires a valid passport for all air arrivals, and many Boynton Beach families travel with minors whose five-year passports expire faster than expected.",
      urgency: "Summer family visits to Canada are often planned in spring — passport checks should happen in March or April",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "Israel",
      context: "The Jewish community in Boynton Beach and southern Palm Beach County frequently travels to Israel for bar and bat mitzvah celebrations, heritage tours, religious pilgrimages, and family visits. Group trips are common, with multiple families coordinating travel dates, hotel bookings, and tour guides well in advance. Israel requires six months of passport validity beyond your planned return date.",
      urgency: "Bar/bat mitzvah trips are planned 6–12 months ahead but booked in detail 3–6 months before departure — passport surprises derail entire group itineraries",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "United Kingdom & Europe",
      context: "London, Paris, Rome, and Barcelona draw Boynton Beach retirees on cultural tours, river cruises, and extended summer vacations. Mediterranean cruises depart from Barcelona and Rome, requiring valid passports for every port of call. The Schengen Area requires three months of validity beyond your planned departure, and many European countries enforce the six-month rule more strictly than the minimum.",
      urgency: "European tour deposits and cruise bookings are typically non-refundable with short cancellation windows",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "Latin America",
      context: "Sao Paulo, Rio de Janeiro, Bogota, and Panama City draw Boynton Beach business travelers in healthcare, finance, import-export, and real estate. Costa Rica and Belize attract eco-tourism and adventure travelers from the younger professional demographic. Cuba and the Dominican Republic see heritage travel from the Caribbean communities in the area. Each country has its own passport validity requirements, typically six months.",
      urgency: "Business trips to Latin America are often booked with 2–4 weeks of notice — too short for standard processing",
    },
  ];

  return (
    <section id="destinations" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Where Boynton Beach Travelers Go</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Popular International Destinations from Boynton Beach
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
            Boynton Beach travelers head to the Caribbean, Mexico, Canada, Europe, Israel, and Latin America for a mix of cruises, family visits, business travel, and cultural experiences. These are the destinations that create the most passport urgency for our Boynton Beach clients.
          </p>
        </div>

        {/* Stat blocks */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-14">
          {stats.map((stat, i) => (
            <div key={i} className="bg-[#F7F8FA] rounded-lg p-5 border border-gray-200 text-center">
              <p className="font-serif text-2xl md:text-3xl font-bold text-[#1A365D] mb-1">{stat.value}</p>
              <p className="text-gray-500 text-xs leading-relaxed">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* Destination cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
          {destinations.map((dest, i) => (
            <div
              key={i}
              className="bg-[#F7F8FA] rounded-lg border border-gray-200 p-6 hover:border-[#F6C344] transition-all duration-200 flex flex-col"
            >
              <div className="flex items-center gap-2.5 mb-3">
                <div className="w-9 h-9 flex items-center justify-center rounded-full bg-white border border-gray-200 flex-shrink-0">
                  <i className={`${dest.icon} text-[#1A365D] text-sm`}></i>
                </div>
                <h3 className="font-bold text-[#1A365D] text-sm">{dest.name}</h3>
              </div>
              <p className="text-gray-600 text-sm leading-relaxed mb-4 flex-1">{dest.context}</p>
              <div className="bg-white rounded-md p-3 border border-gray-200">
                <p className="text-[#F6C344] text-xs font-semibold mb-0.5"><i className="ri-time-line mr-1"></i>Urgency Pattern</p>
                <p className="text-gray-500 text-xs leading-relaxed">{dest.urgency}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Internal links */}
        <div className="text-center">
          <p className="text-gray-400 text-xs mb-3">Serving travelers throughout South Florida</p>
          <div className="flex flex-wrap items-center justify-center gap-2">
            {[
              { name: "Delray Beach", href: "/cities/delray-beach" },
              { name: "West Palm Beach", href: "/cities/west-palm-beach" },
              { name: "Boca Raton", href: "/cities/boca-raton" },
              { name: "Palm Beach", href: "/cities/palm-beach" },
              { name: "Fort Lauderdale", href: "/cities/hollywood" },
              { name: "Naples", href: "/cities/naples" },
            ].map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="inline-flex items-center gap-1 text-xs text-[#1A365D] hover:text-[#F6C344] font-medium transition-colors bg-[#F7F8FA] border border-gray-200 hover:border-[#F6C344] rounded-full px-3 py-1.5"
              >
                {link.name} <i className="ri-arrow-right-line text-[10px]"></i>
              </a>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}